import React from 'react';
import { Box, Typography, Avatar, IconButton, Chip } from '@mui/material';
import { MoreVert as MoreVertIcon, LinkedIn as LinkedInIcon } from '@mui/icons-material';
import type { LeadListItem } from '@/api/leads';

interface LeadCardProps {
  item: LeadListItem;
  onCardClick: (uuid: string) => void;
}

export const LeadCard: React.FC<LeadCardProps> = ({ item, onCardClick }) => {
  const p = item.prospect;
  const image = typeof p?.profile_image === 'string' ? p?.profile_image : p?.profile_image?.url;
  const linkedin = p?.linkedin_public_url || p?.linkedin_url || '';

  return (
    <Box
      onClick={() => onCardClick(p?.key || item.key)}
      sx={{
        py: 2,
        px: 1,
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      {/* Name row with LinkedIn icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Avatar src={image || undefined} sx={{ width: 40, height: 40 }} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600 }}
            noWrap
            title={p?.full_name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim()}
          >
            {p?.full_name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim() || 'Unknown'}
          </Typography>
          {linkedin && (
            <IconButton
              href={linkedin}
              target="_blank"
              rel="noreferrer"
              size="small"
              color="primary"
              onClick={(e) => e.stopPropagation()}
            >
              <LinkedInIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box onClick={(e) => e.stopPropagation()}>
          <IconButton size="small">
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Title row */}
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }} noWrap title={p?.title || ''}>
        {p?.title || '-'}
      </Typography>

      {/* Location row */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1.5, display: 'block' }}
        title={p?.location || ''}
      >
        {p?.location || '-'}
      </Typography>

      {/* Current Company */}
      <Typography variant="body2" sx={{ mb: 1.5 }} noWrap title={p?.current_company?.name || ''}>
        {p?.current_company?.name || '-'}
      </Typography>

      {/* Step Categories */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {(item.step_categories || []).map((c) => (
          <Chip key={c} label={c} size="small" variant="outlined" />
        ))}
      </Box>

      {/* Activities Summary */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1.5, display: 'block', whiteSpace: 'pre-line' }}
      >
        {item.activity_summary || '-'}
      </Typography>
    </Box>
  );
};

